import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ModalSheet from '../modal-sheet/modal-sheet.component';
import { connect } from 'react-redux';
import { Button } from 'reactstrap';
import {
  setCheckout,
  setCart,
  setOrder,
  setOrderHash,
} from '../../../../redux/store/store.actions';
import {
  setIsCheckoutModalOpen,
  setIsOrderInfoModalOpen,
  setIsPickupStatusModalOpen,
  setIsSignUpNoticeModalOpen,
  setOrderInfoModalData,
} from '../../../../redux/modal/modal.actions';
import PropTypes from 'prop-types';
import { updateOrder } from './checkout.service';
import {
  fetchOnAuthenticated,
  fetchOrders,
  fetchPaymentMethods,
  setPickupOrder,
  fetchMe,
} from '../../../../redux/me/me.actions';
import {
  createHash,
  getLocalizedName,
  replaceColorInLottie,
} from '../../../../utility/util/util.service';
import DefaultAlert from '../../alerts/default-alert/default-alert.component';
import { config } from '../../../../configs/app.config';
import {
  CHECKOUT_METHOD_PAY_AND_GO,
  ORDER_STATUS_PENDING,
  ORDER_TYPE_INHOUSE,
} from '../../../../utility/constants';
import TagManager from 'react-gtm-module/dist/TagManager';
import { useHistory } from 'react-router-dom';
import paymentTerminal from '../../../../assets/lottie/payment-terminal-1.json';
import Lottie from 'lottie-react';
import {
  baseDeleteQuery,
  basePostQuery,
} from '../../../../utility/sb-api/sb-api.service';

const TerminalCheckoutModal = ({
  isOpen,
  cart,
  store,
  checkout,
  me,
  order,
  orderHash,
  paymentMethods,
  table,
  kiosk,
  userOrders,

  setIsCheckoutModalOpen,
  setCheckout,
  setCart,
  setOrder,
  setOrderHash,
  fetchOrders,
  setIsOrderInfoModalOpen,
  setOrderInfoModalData,
  fetchPaymentMethods,
  setIsSignUpNoticeModalOpen,
  fetchMe,
}) => {
  const intl = useIntl();
  const history = useHistory();

  const [isOrderUpdating, setIsOrderUpdating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRetryingPayment, setIsRetryingPayment] = useState(false);

  // reset states on modal close
  useEffect(() => {
    if (!isOpen) {
      setIsSubmitting(false);
      setIsOrderUpdating(false);
      setIsRetryingPayment(false);
    }
  }, [isOpen]);

  const handleRetryPayment = async () => {
    if (!order?.id || isRetryingPayment) return;

    setIsRetryingPayment(true);
    try {
      await baseDeleteQuery(`user/orders/${order?.id}/terminal-payment`);
      await basePostQuery(`user/orders/${order?.id}/terminal-payment`);
    } catch (error) {
      console.error('Error retrying payment:', error);
    } finally {
      setIsRetryingPayment(false);
    }
  };

  useEffect(() => {
    const hash = createHash({ cart: cart, checkout: checkout });
    if (isOpen && kiosk && hash === orderHash) {
      basePostQuery(`user/orders/${order?.id}/terminal-payment`);
    }
  }, [isOpen, kiosk]);

  // listen for OrderStatus != pending
  useEffect(() => {
    if (order) {
      const updatedOrder = userOrders?.find(({ id }) => id === order.id);

      if (
        isOpen &&
        updatedOrder &&
        updatedOrder.status !== ORDER_STATUS_PENDING
      ) {
        setOrderInfoModalData({ order: updatedOrder, isInitial: true });
        setIsOrderInfoModalOpen(true);
        setCheckout(null);
        setCart(null);
        setOrder(null);
        setOrderHash(null);
      }
    }
  }, [userOrders]);

  // updates the order if the orderHash changes (e.g. tip added)
  useEffect(() => {
    if (isOpen && order && !isSubmitting) {
      const hash = createHash({ cart: cart, checkout: checkout });

      // update order if hashes are not equal (something changed)
      if (hash !== orderHash) {
        setIsOrderUpdating(true);
        updateOrder(
          me,
          order,
          checkout,
          cart,
          store,
          table,
          kiosk,
          setOrder,
          setOrderHash,
          intl,
          fetchMe,
        )
          .then(({ error }) => {
            setIsOrderUpdating(false);
            if (error) throw new Error(error.message);
            basePostQuery(`user/orders/${order?.id}/terminal-payment`);
          })
          .catch((err) => {
            console.error(err);
            setIsCheckoutModalOpen(false);
          });
      }
    }
  }, [
    cart,
    checkout,
    isOpen,
    isSubmitting,
    me,
    order,
    orderHash,
    setIsCheckoutModalOpen,
    setOrder,
    setOrderHash,
    store,
  ]);

  return (
    <ModalSheet
      isOpen={!!isOpen}
      closeModal={() => {
        // empty cart if it was a PAY_AND_GO order
        if (
          order.type === ORDER_TYPE_INHOUSE &&
          checkout.checkoutMethod === CHECKOUT_METHOD_PAY_AND_GO
        ) {
          setOrder(null);
          setCart(null);
        }
        if (isOpen && kiosk) {
          baseDeleteQuery(`user/orders/${order?.id}/terminal-payment`);
        }
        setIsCheckoutModalOpen(false);
      }}
      title={intl.formatMessage({ id: 'checkoutTitle' })}
    >
      <div className="px-1">
        {['dev', 'demo'].includes(config.env) && (
          <DefaultAlert
            color="warning"
            msg={intl.formatMessage({ id: 'demoModeCheckoutPaymentAlert' })}
          />
        )}
        <div className="d-flex justify-content-center">
          <Lottie
            style={{ width: '350px' }}
            rendererSettings={{
              viewBoxSize: '-50 100 500 400',
              viewBoxOnly: true,
            }}
            animationData={replaceColorInLottie(
              paymentTerminal,
              store?.primaryColor || '#22442c',
            )}
            loop={true}
          />
        </div>
        <div className="d-block text-center mb-2">
          <span className="font-medium-2 black">
            <FormattedMessage id="payAtTerminalTitle" />
          </span>
        </div>
        <div className="d-flex justify-content-center my-2">
          <Button
            color="primary"
            onClick={handleRetryPayment}
            disabled={isRetryingPayment || !order?.id}
          >
            {isRetryingPayment ? (
              <FormattedMessage id="retrying" />
            ) : (
              <FormattedMessage id="retryPayment" />
            )}
          </Button>
        </div>
      </div>
    </ModalSheet>
  );
};

TerminalCheckoutModal.propTypes = {
  isOpen: PropTypes.bool,
  cart: PropTypes.array,
  store: PropTypes.object,
  table: PropTypes.object,
  checkout: PropTypes.object,
  order: PropTypes.object,
  orderHash: PropTypes.string,
  me: PropTypes.object,
  setIsCheckoutModalOpen: PropTypes.func,
  setCheckout: PropTypes.func,
  setCart: PropTypes.func,
  setOrder: PropTypes.func,
  setOrderHash: PropTypes.func,
  setIsOrderInfoModalOpen: PropTypes.func,
  setOrderInfoModalData: PropTypes.func,
};

const mapStateToProps = (state) => ({
  isOpen: state.modals.isCheckoutModalOpen,
  cart: state.store.cart,
  store: state.store.store,
  table: state.store.table,
  checkout: state.store.checkout,
  order: state.store.order,
  orderHash: state.store.orderHash,
  paymentMethods: state.me.paymentMethods,
  me: state.me.me,
  userOrders: state.me.orders,
  kiosk: state.store.kiosk,
});

export default connect(mapStateToProps, {
  setIsCheckoutModalOpen,
  setCheckout,
  setCart,
  setPickupOrder,
  setIsPickupStatusModalOpen,
  fetchOnAuthenticated,
  setOrder,
  setOrderHash,
  fetchOrders,
  setIsOrderInfoModalOpen,
  setOrderInfoModalData,
  fetchPaymentMethods,
  setIsSignUpNoticeModalOpen,
  fetchMe,
})(TerminalCheckoutModal);
